import React from 'react';
import { motion } from 'framer-motion';
export function ComingSoon() {
  return <section id="coming-soon" className="bg-white py-10 lg:py-14" data-id="element-6">
      <div className="mx-3 sm:mx-6 rounded-[40px] bg-forest relative overflow-hidden" data-id="element-7">
        {/* subtle dotted texture */}
        <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #FFFFFF 1px, transparent 1px)',
        backgroundSize: '26px 26px'
      }} aria-hidden="true" data-id="element-8" />

        <div className="relative px-5 sm:px-8 pt-14 lg:pt-16 text-center" data-id="element-9">
          <span className="eyebrow text-forest" data-id="element-10">More Coming Soon</span>
        </div>

        {/* ===== Animated farm scene ===== */}
        <div className="relative mt-8 h-64 sm:h-80 lg:h-[26rem] overflow-hidden" role="img" aria-label="A tractor ploughing through a field, dragging a Coming Soon banner" data-id="element-11">
          {/* tilled ground band */}
          <div className="absolute inset-x-0 bottom-0 h-28 sm:h-32 bg-forestdeep" data-id="element-12">
            <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none" viewBox="0 0 400 120" fill="none" aria-hidden="true" data-id="element-13">
              {[18, 42, 66, 90, 110].map(y => <path key={y} d={`M0 ${y} Q100 ${y - 6} 200 ${y} T400 ${y}`} stroke="#2F3A1C" strokeWidth="2" data-id="element-14" />)}
            </svg>
          </div>

          {/* moving rig: banner (trailing) + tractor */}
          <motion.div className="absolute bottom-[3.2rem] sm:bottom-[3.8rem] left-0 flex items-end gap-3 sm:gap-5" initial={{
          x: '-45%'
        }} animate={{
          x: '108%'
        }} transition={{
          duration: 9,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-15">
            {/* dragged "Coming Soon" banner */}
            <div className="relative -rotate-2 origin-bottom-right" data-id="element-16">
              <div className="bg-gold text-forestdeep rounded-2xl px-5 sm:px-8 py-3 sm:py-4 shadow-2xl ring-4 ring-golddark/30" data-id="element-17">
                <span className="font-display text-2xl sm:text-4xl lg:text-5xl whitespace-nowrap tracking-tight" data-id="element-18">
                  Coming Soon
                </span>
              </div>
              {/* tow line from banner to tractor */}
              <span className="absolute top-1/2 -right-3 sm:-right-5 w-3 sm:w-5 h-[3px] bg-goldlight" data-id="element-19" />
            </div>

            {/* tractor */}
            <Tractor data-id="element-20" />
          </motion.div>
        </div>

        <div className="relative px-5 sm:px-8 pb-14 lg:pb-16 text-center" data-id="element-21">
          <p className="text-paper/70 text-sm sm:text-base max-w-md mx-auto" data-id="element-22">
            We're ploughing ahead — the full AFEXA experience is on its way.
          </p>
        </div>
      </div>
    </section>;
}
function Tractor() {
  const wheelSpin = {
    animate: {
      rotate: 360
    },
    transition: {
      duration: 1.1,
      repeat: Infinity,
      ease: 'linear' as const
    }
  };
  return <svg width="150" height="110" viewBox="0 0 150 110" fill="none" className="w-28 sm:w-36 lg:w-44 h-auto drop-shadow-xl flex-shrink-0" aria-hidden="true" data-id="element-23">
      {/* exhaust puffs */}
      {[0, 1, 2].map(i => <motion.circle key={i} cx={44} cy={26} r={4} fill="#FFFFFF" initial={{
      opacity: 0,
      cy: 30,
      cx: 44
    }} animate={{
      opacity: [0, 0.5, 0],
      cy: 6,
      cx: 38
    }} transition={{
      duration: 1.4,
      repeat: Infinity,
      delay: i * 0.45,
      ease: 'easeOut'
    }} data-id="element-24" />)}

      {/* exhaust stack */}
      <rect x="41" y="30" width="6" height="20" rx="2" fill="#424D27" data-id="element-25" />

      {/* chassis */}
      <rect x="30" y="58" width="86" height="12" rx="4" fill="#424D27" data-id="element-26" />

      {/* engine hood (front) */}
      <path d="M92 58 L92 44 Q92 40 96 40 L120 40 Q126 40 126 46 L126 58 Z" fill="#FFC500" data-id="element-27" />
      <rect x="120" y="46" width="8" height="10" rx="2" fill="#6E7D46" data-id="element-28" />
      {/* grille lines */}
      <g stroke="#424D27" strokeWidth="1.5" data-id="element-29">
        <line x1="122" y1="48" x2="127" y2="48" data-id="element-30" />
        <line x1="122" y1="52" x2="127" y2="52" data-id="element-31" />
      </g>

      {/* cab */}
      <path d="M52 58 L52 30 Q52 26 56 26 L84 26 Q88 26 88 30 L88 58 Z" fill="#FFC500" data-id="element-32" />
      {/* cab window */}
      <rect x="58" y="32" width="24" height="18" rx="3" fill="#EFF2E0" data-id="element-33" />
      <rect x="58" y="32" width="24" height="18" rx="3" stroke="#424D27" strokeWidth="2" data-id="element-34" />

      {/* rear wheel */}
      <motion.g style={{
      transformBox: 'fill-box',
      transformOrigin: 'center'
    }} animate={{
      rotate: 360
    }} transition={wheelSpin.transition} data-id="element-35">
        <circle cx="46" cy="80" r="24" fill="#424D27" data-id="element-36" />
        <circle cx="46" cy="80" r="11" fill="#6E7D46" data-id="element-37" />
        <circle cx="46" cy="80" r="4" fill="#FFD84D" data-id="element-38" />
        {/* spokes / treads */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map(a => <rect key={a} x="44.5" y="56" width="3" height="8" rx="1" fill="#7E8E4E" transform={`rotate(${a} 46 80)`} data-id="element-39" />)}
      </motion.g>

      {/* front wheel */}
      <motion.g style={{
      transformBox: 'fill-box',
      transformOrigin: 'center'
    }} animate={{
      rotate: 360
    }} transition={wheelSpin.transition} data-id="element-40">
        <circle cx="112" cy="88" r="15" fill="#424D27" data-id="element-41" />
        <circle cx="112" cy="88" r="6" fill="#6E7D46" data-id="element-42" />
        <circle cx="112" cy="88" r="2.5" fill="#FFD84D" data-id="element-43" />
        {[0, 60, 120, 180, 240, 300].map(a => <rect key={a} x="111" y="73" width="2" height="6" rx="1" fill="#7E8E4E" transform={`rotate(${a} 112 88)`} data-id="element-44" />)}
      </motion.g>
    </svg>;
}